import React, { useState, useEffect } from 'react';
import {
  Download,
  X,
  Smartphone,
  Laptop,
  CheckCircle2,
  ExternalLink,
  ChevronRight,
  Share2,
  PlusSquare,
  Compass,
  Monitor,
  Sparkles,
  WifiOff,
  ShieldCheck,
  Minimize2,
  Maximize2,
  HelpCircle
} from 'lucide-react';
import { PWAInstallService } from '../../services/mobile/pwaInstallService';

export interface PWAInstallBannerProps {
  className?: string;
  forceShowToast?: boolean;
}

type BrowserType = 'chrome' | 'safari-ios' | 'safari-mac' | 'edge' | 'firefox';

export const PWAInstallBanner: React.FC<PWAInstallBannerProps> = ({
  className = '',
}) => {
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [canPromptInstall, setCanPromptInstall] = useState<boolean>(false);
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('yamoopay_pwa_banner_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [viewMode, setViewMode] = useState<'banner' | 'toast'>('banner');
  const [showInstructionsModal, setShowInstructionsModal] = useState<boolean>(false);
  const [selectedBrowser, setSelectedBrowser] = useState<BrowserType>('chrome');

  // Detect browser environment on mount
  useEffect(() => {
    const standalone = PWAInstallService.isStandalone();
    setIsStandalone(standalone);

    const unsubscribe = PWAInstallService.subscribe((eligible) => {
      setCanPromptInstall(eligible);
    });

    // Auto-detect browser
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const ua = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) {
        setSelectedBrowser('safari-ios');
      } else if (/macintosh/.test(ua) && /safari/.test(ua) && !/chrome|crios/.test(ua)) {
        setSelectedBrowser('safari-mac');
      } else if (/edg/.test(ua)) {
        setSelectedBrowser('edge');
      } else if (/firefox|fxios/.test(ua)) {
        setSelectedBrowser('firefox');
      } else {
        setSelectedBrowser('chrome');
      }
    }

    return () => unsubscribe();
  }, []);

  // If already running as installed PWA, do not show banner or toast
  if (isStandalone || isDismissed) {
    return null;
  }

  const handleInstallClick = async () => {
    if (canPromptInstall) {
      const result = await PWAInstallService.promptInstall();
      if (result === 'ACCEPTED') {
        setIsDismissed(true);
        return;
      }
    }
    // If prompt is unavailable (e.g. Safari, Firefox, or in sandbox), open browser guide
    setShowInstructionsModal(true);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      sessionStorage.setItem('yamoopay_pwa_banner_dismissed', 'true');
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* 1. TOP HEADER BANNER MODE */}
      {viewMode === 'banner' && (
        <div className={`bg-linear-to-r from-[#0E7044] via-[#0B5C37] to-[#1A1A1A] text-white px-4 py-2.5 sm:py-2 text-xs transition-all relative border-b border-emerald-800/40 shadow-xs ${className}`}>
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            {/* Left Content */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Download className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                <span className="font-bold text-white whitespace-nowrap">
                  Install YamooPay App:
                </span>
                <span className="text-emerald-100/90 text-[11px] truncate">
                  Get 100% offline access, zero-latency financial calculators, and full standalone window mode.
                </span>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
              <button
                onClick={handleInstallClick}
                className="px-3 py-1 bg-white hover:bg-emerald-50 text-[#0E7044] font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer text-xs"
              >
                <Download className="w-3 h-3" />
                <span>{canPromptInstall ? 'Install Now' : 'Install Guide'}</span>
              </button>

              <button
                onClick={() => setShowInstructionsModal(true)}
                className="px-2.5 py-1 bg-white/10 hover:bg-white/20 text-emerald-100 hover:text-white font-medium rounded-lg transition-colors flex items-center gap-1 cursor-pointer text-xs"
                title="View browser-specific installation instructions"
              >
                <HelpCircle className="w-3 h-3" />
                <span className="hidden md:inline">Browser Instructions</span>
              </button>

              {/* Minimize to bottom toast */}
              <button
                onClick={() => setViewMode('toast')}
                className="p-1 text-emerald-200/80 hover:text-white hover:bg-white/10 rounded-md transition-colors cursor-pointer"
                title="Minimize to bottom toast"
              >
                <Minimize2 className="w-3.5 h-3.5" />
              </button>

              {/* Dismiss */}
              <button
                onClick={handleDismiss}
                className="p-1 text-emerald-200/80 hover:text-white hover:bg-white/10 rounded-md transition-colors cursor-pointer"
                title="Dismiss banner for this session"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. FLOATING BOTTOM TOAST MODE */}
      {viewMode === 'toast' && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-5 sm:bottom-5 z-40 max-w-sm mx-auto sm:mx-0 w-auto sm:w-full bg-[#1A1A1A] text-white border border-[#333333] rounded-2xl shadow-2xl p-4 animate-in slide-in-from-bottom-5 duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#0E7044] text-white flex items-center justify-center shrink-0 shadow-xs">
                <Download className="w-4 h-4" />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-white">Install YamooPay PWA</h4>
                <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
                  Fast desktop & mobile app experience with offline capability and instant launch.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setViewMode('banner')}
                className="p-1 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-md transition-colors"
                title="Expand to top banner"
              >
                <Maximize2 className="w-3 h-3" />
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-md transition-colors"
                title="Dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-[#333333] flex items-center justify-between gap-2">
            <button
              onClick={() => setShowInstructionsModal(true)}
              className="text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"
            >
              <span>Browser Instructions</span>
              <ChevronRight className="w-3 h-3" />
            </button>
            <button
              onClick={handleInstallClick}
              className="px-3.5 py-1.5 bg-[#0E7044] hover:bg-[#0B5C37] text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 shadow-xs"
            >
              <Download className="w-3.5 h-3.5" />
              <span>{canPromptInstall ? 'Install App' : 'Get App'}</span>
            </button>
          </div>
        </div>
      )}

      {/* 3. BROWSER INSTRUCTIONS MODAL */}
      {showInstructionsModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs animate-in fade-in duration-150"
          onClick={() => setShowInstructionsModal(false)}
        >
          <div 
            className="w-full max-w-xl bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-[#E5E7EB] flex items-center justify-between bg-[#FBFBFC]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-bold text-sm">
                  YP
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1A1A1A]">
                    Install YamooPay on Your Device
                  </h3>
                  <p className="text-xs text-[#6B7280]">
                    Select your browser below for step-by-step installation instructions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowInstructionsModal(false)}
                className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Browser Selection Tabs */}
            <div className="flex border-b border-[#E5E7EB] px-5 bg-[#F9FAFB] overflow-x-auto gap-1">
              {[
                { id: 'chrome', label: 'Chrome / Brave' },
                { id: 'safari-ios', label: 'Safari (iPhone/iPad)' },
                { id: 'safari-mac', label: 'Safari (macOS)' },
                { id: 'edge', label: 'Microsoft Edge' },
                { id: 'firefox', label: 'Firefox' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedBrowser(tab.id as BrowserType)}
                  className={`py-2.5 px-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors cursor-pointer ${
                    selectedBrowser === tab.id
                      ? 'border-[#0E7044] text-[#0E7044]'
                      : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Modal Body: Instructions Content */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              {/* CHROME / BRAVE / CHROMIUM */}
              {selectedBrowser === 'chrome' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-xl flex items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="text-xs font-bold text-emerald-950">
                        1-Click Direct Installation
                      </div>
                      <p className="text-[11px] text-emerald-800">
                        YamooPay supports instant Progressive Web App deployment on Chromium browsers.
                      </p>
                    </div>
                    {canPromptInstall && (
                      <button
                        onClick={async () => {
                          const res = await PWAInstallService.promptInstall();
                          if (res === 'ACCEPTED') {
                            setShowInstructionsModal(false);
                            setIsDismissed(true);
                          }
                        }}
                        className="px-3.5 py-1.5 bg-[#0E7044] hover:bg-[#0B5C37] text-white text-xs font-bold rounded-xl shrink-0 transition-colors shadow-xs"
                      >
                        Install Now
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">
                      Desktop (Windows, Mac, Linux, Chromebook):
                    </div>
                    <ol className="space-y-2.5 text-xs text-[#4B5563]">
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
                        <span>Look at the right side of the address bar (URL bar). Click the <strong>Install YamooPay</strong> icon (computer monitor with a downward arrow).</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
                        <span>Alternatively, click the <strong>three vertical dots (⋮)</strong> in the top right &gt; select <strong>"Save and share"</strong> &gt; click <strong>"Install YamooPay"</strong>.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">3</span>
                        <span>Click <strong>Install</strong> in the confirmation popup. YamooPay will open in its own sleek, independent desktop window.</span>
                      </li>
                    </ol>

                    <div className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider pt-2">
                      Android (Chrome / Brave / Samsung Internet):
                    </div>
                    <ol className="space-y-2.5 text-xs text-[#4B5563]">
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
                        <span>Tap the <strong>three dots menu (⋮)</strong> in the upper right corner of Chrome.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
                        <span>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">3</span>
                        <span>Confirm by tapping <strong>Install</strong>. YamooPay icon will be added to your home screen and app drawer.</span>
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {/* SAFARI (iOS - IPHONE / IPAD) */}
              {selectedBrowser === 'safari-ios' && (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl space-y-1 text-xs">
                    <div className="font-bold text-amber-950 flex items-center gap-1.5">
                      <Share2 className="w-4 h-4 text-amber-700" />
                      <span>iOS Safari Installation</span>
                    </div>
                    <p className="text-[11px] text-amber-900 leading-relaxed">
                      Apple Safari on iOS does not support programmatic install prompts. You can install YamooPay in 2 quick taps via the standard iOS Share Menu.
                    </p>
                  </div>

                  <ol className="space-y-3 text-xs text-[#4B5563]">
                    <li className="p-3 bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white font-bold flex items-center justify-center shrink-0 text-xs">1</div>
                      <div className="space-y-0.5">
                        <span className="font-bold text-[#1A1A1A] block">Tap the Share Icon</span>
                        <p className="text-[11px] text-[#6B7280]">
                          At the bottom of your iPhone screen (or top of iPad), tap the <strong>Share</strong> button (a square with an arrow pointing upward).
                        </p>
                      </div>
                    </li>

                    <li className="p-3 bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white font-bold flex items-center justify-center shrink-0 text-xs">2</div>
                      <div className="space-y-0.5">
                        <span className="font-bold text-[#1A1A1A] block">Select "Add to Home Screen"</span>
                        <p className="text-[11px] text-[#6B7280]">
                          Scroll down through the share options sheet and tap <strong>"Add to Home Screen"</strong> (indicated with a plus sign icon).
                        </p>
                      </div>
                    </li>

                    <li className="p-3 bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white font-bold flex items-center justify-center shrink-0 text-xs">3</div>
                      <div className="space-y-0.5">
                        <span className="font-bold text-[#1A1A1A] block">Tap "Add" in Top Right</span>
                        <p className="text-[11px] text-[#6B7280]">
                          Confirm the name "YamooPay" and tap <strong>Add</strong>. The app icon will appear immediately on your iPhone / iPad home screen!
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>
              )}

              {/* SAFARI (macOS SONOMA / SEQUOIA) */}
              {selectedBrowser === 'safari-mac' && (
                <div className="space-y-4 text-xs">
                  <div className="p-4 bg-sky-50/70 border border-sky-200 rounded-xl space-y-1">
                    <div className="font-bold text-sky-950 flex items-center gap-1.5">
                      <Monitor className="w-4 h-4 text-sky-700" />
                      <span>macOS Web Apps in Dock</span>
                    </div>
                    <p className="text-[11px] text-sky-900 leading-relaxed">
                      In macOS Sonoma (14.0+) or Sequoia (15.0+), Safari allows turning any web application into a native Mac Dock app.
                    </p>
                  </div>

                  <ol className="space-y-3 text-[#4B5563]">
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
                      <span>In Safari's top Mac menu bar, click <strong>File</strong>.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
                      <span>Click <strong>"Add to Dock..."</strong>.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">3</span>
                      <span>In the confirmation dialog, ensure the name is <strong>YamooPay</strong> and click <strong>Add</strong>.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">4</span>
                      <span>YamooPay will open directly from your macOS Dock in a native, standalone window with its own Dock icon!</span>
                    </li>
                  </ol>
                </div>
              )}

              {/* MICROSOFT EDGE */}
              {selectedBrowser === 'edge' && (
                <div className="space-y-4 text-xs">
                  <div className="p-4 bg-blue-50/70 border border-blue-200 rounded-xl space-y-1">
                    <div className="font-bold text-blue-950">
                      Native Windows & Edge App Integration
                    </div>
                    <p className="text-[11px] text-blue-900">
                      Microsoft Edge features native PWA integration with the Windows Start Menu, Taskbar, and notification center.
                    </p>
                  </div>

                  <ol className="space-y-2.5 text-[#4B5563]">
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
                      <span>Click the <strong>App available (+)</strong> icon in the Edge address bar (right side).</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
                      <span>Or click <strong>Settings and more (...)</strong> &gt; <strong>Apps</strong> &gt; <strong>"Install YamooPay"</strong>.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">3</span>
                      <span>Click <strong>Install</strong>. You can pin YamooPay to your Windows taskbar or Start menu for instant 1-click access.</span>
                    </li>
                  </ol>
                </div>
              )}

              {/* MOZILLA FIREFOX */}
              {selectedBrowser === 'firefox' && (
                <div className="space-y-4 text-xs">
                  <div className="p-4 bg-orange-50/70 border border-orange-200 rounded-xl space-y-1">
                    <div className="font-bold text-orange-950">
                      Firefox PWA & Shortcut Options
                    </div>
                    <p className="text-[11px] text-orange-900">
                      On mobile, Firefox supports full PWA installation. On desktop, you can pin the tab or use standard desktop shortcuts.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="font-bold text-[#1A1A1A]">Firefox on Android:</div>
                    <ol className="space-y-2 text-[#4B5563]">
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
                        <span>Tap the <strong>three dots menu (⋮)</strong> next to the address bar.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#1A1A1A] font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
                        <span>Tap <strong>"Install"</strong> or <strong>"Add to Home screen"</strong>.</span>
                      </li>
                    </ol>

                    <div className="font-bold text-[#1A1A1A] pt-2">Firefox Desktop:</div>
                    <p className="text-[#4B5563] text-[11px]">
                      Right click the YamooPay browser tab and select <strong>"Pin Tab"</strong> for persistent zero-overhead access.
                    </p>
                  </div>
                </div>
              )}

              {/* Benefits Checklist */}
              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Why Install YamooPay?</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-[#4B5563]">
                  <div className="flex items-center gap-1.5">
                    <WifiOff className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>Works 100% offline</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>Zero server tracking</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>Instant, zero-lag launch</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>No App Store download</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-[#FBFBFC] border-t border-[#E5E7EB] flex items-center justify-between">
              <span className="text-xs text-[#6B7280]">
                Powered by Progressive Web App (PWA) Standards
              </span>
              <button
                onClick={() => setShowInstructionsModal(false)}
                className="px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
